/*
 * F2 : indexation unique du snapshot. LIRE AVANT D'ÉCRIRE UNE PAGE (lots 3, 4, 5).
 *
 * Mémoïsé (jamais recalculé tant que les références d'entrée ne changent pas) :
 *   - indexer(snapshot, {collecte})                : par (identité du snapshot, collecte) (WeakMap) ; ~30 ms sur 70 000 événements.
 *     `collecte` (défaut false) : bouton « Collecte » (lot 8). À false, les événements `source === 'simulation'`
 *     (COLLECTE, COLLECTE_IMPOSSIBLE) sont exclus de l'index : les écrans sont identiques à ceux d'avant le lot 8.
 *     `index.collecte` porte le choix ; `bornes` ignore toujours les événements de collecte.
 *   - etatsDesPav(index, {acte, du, au, at})       : cache LRU de 8 jeux de paramètres par index (etat.js).
 *   - evenementsRetenus(index, pavs, {du, au, at}) : même LRU, par (index, référence du tableau `pavs`, paramètres).
 *   - rseRetenue(index, pavs, params)              : idem, via evenementsRetenus (rse.js).
 *
 * NON mémoïsé (bon marché, 0 à 20 ms sur le parc réel) mais à mettre dans un `useMemo` côté page :
 *   - filtrerPavs, kpi, repartitions, alertes, toCsv, toGeoJson.
 *   Règle : `useMemo(() => filtrerPavs(index, etats, filtres), [index, etats, filtres])`. Les sélecteurs mémoïsés
 *   ci-dessus se basent sur l'identité du tableau `pavs`, qui doit donc rester stable d'un rendu à l'autre
 *   (sinon le cache ne sert à rien). `filtres` et la plage doivent eux-mêmes être stables (useMemo / état).
 *
 * Typologies de maintenance : `index.typologiesMaintenanceParPatrimoine` (Map<patrimoineId, Set>) = `typologiesMaintenance`
 * exporté par l'API sur chaque patrimoine (PAT-04 R2 ; absent = vide). En acte `maint`, un IMPOSSIBLE n'est candidat que si
 * sa `detail.typologie` est dans le set du patrimoine de l'événement (`evenement.patrimoineId`) (B4).
 *
 * Communes : chaque PAV indexé reçoit `communeCle` (clé normalisée) et `communeLibelle` (graphie brute la plus
 * fréquente pour la clé) ; `commune` brute reste disponible. Le filtre `commune` du permalien est une CLÉ.
 */

import {couleurRang} from './circuits.js';

const cache = new WeakMap();

export function comparerEvenements(a, b) {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    const ha = a.heure || '';
    const hb = b.heure || '';
    if (ha !== hb) return ha < hb ? -1 : 1;
    return 0;
}

/** Clé de commune : sans accents, majuscules, tirets -> espaces, espaces réduits, ST/STE -> SAINT/SAINTE en préfixe. */
export function cleCommune(brut) {
    if (brut == null) return null;
    const cle = String(brut)
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^ST /, 'SAINT ')
        .replace(/^STE /, 'SAINTE ');
    return cle === '' ? null : cle;
}

function indexerCommunes(pavsBruts) {
    const graphies = new Map(); // cle -> Map<graphie brute, nb>
    for (const p of pavsBruts) {
        const cle = cleCommune(p.commune);
        if (cle === null) continue;
        let m = graphies.get(cle);
        if (!m) graphies.set(cle, (m = new Map()));
        m.set(p.commune, (m.get(p.commune) || 0) + 1);
    }
    const communes = [];
    for (const [cle, m] of graphies) {
        const choisie = [...m.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))[0][0];
        communes.push({cle, libelle: choisie, nbPav: [...m.values()].reduce((a, b) => a + b, 0)});
    }
    communes.sort((a, b) => a.libelle.localeCompare(b.libelle));
    return communes;
}

function couleursPatrimoines(patrimoinesBruts) {
    const rangs = new Map([...patrimoinesBruts].sort((a, b) => a.id - b.id).map((p, i) => [p.id, i]));
    return patrimoinesBruts.map((p) => ({...p, couleur: couleurRang(rangs.get(p.id)), couleurClair: couleurRang(rangs.get(p.id), {clair: true})}));
}

export const EST_SIMULE = (e) => e.source === 'simulation';

function construire(snapshot, collecte) {
    const pavsBruts = snapshot.pav || [];
    const communes = indexerCommunes(pavsBruts);
    const communeParCle = new Map(communes.map((c) => [c.cle, c]));
    // Copies : le snapshot RTK Query est figé, on ne l'enrichit pas en place.
    const pavs = pavsBruts.map((p) => {
        const cle = cleCommune(p.commune);
        return {...p, communeCle: cle, communeLibelle: cle === null ? null : communeParCle.get(cle).libelle};
    });
    const patrimoines = couleursPatrimoines((snapshot.territoire && snapshot.territoire.patrimoines) || []);
    const pavById = new Map(pavs.map((p) => [p.id, p]));
    const patrimoineById = new Map(patrimoines.map((p) => [p.id, p]));

    // Array.prototype.sort est stable : à date/heure égales, l'ordre du snapshot est conservé.
    const evenements = (snapshot.evenements || []).filter((e) => collecte || !EST_SIMULE(e)).sort(comparerEvenements);

    const evenementsParPav = new Map();
    const elements = new Set(); // libellés d'éléments de checklist connus
    let min = null;
    let max = null;
    for (const e of evenements) {
        let liste = evenementsParPav.get(e.pavId);
        if (!liste) {
            liste = [];
            evenementsParPav.set(e.pavId, liste);
        }
        liste.push(e);
        if (e.type !== 'PLANIFIÉ' && e.type !== 'COLLECTE' && e.type !== 'COLLECTE_IMPOSSIBLE') {
            if (min === null || e.date < min) min = e.date;
            if (max === null || e.date > max) max = e.date;
        }
        if (e.type === 'MAINT_PREV') {
            for (const el of e.elements || []) elements.add(el.libelle);
        }
    }

    // Typologies de maintenance (PAT-04 R2) : par patrimoine, exportées par l'API ; absence = liste vide.
    const typologiesMaintenanceParPatrimoine = new Map(patrimoines.map((p) => [p.id, new Set(p.typologiesMaintenance ?? [])]));

    return {collecte, typologiesMaintenanceParPatrimoine, pavs, pavById, patrimoineById, evenementsParPav, evenements, bornes: {min, max}, communes, communeParCle, elements, snapshot};
}

export function indexer(snapshot, {collecte = false} = {}) {
    if (!snapshot) return null;
    let parSnapshot = cache.get(snapshot);
    if (!parSnapshot) {
        parSnapshot = {};
        cache.set(snapshot, parSnapshot);
    }
    const cle = collecte ? 'avec' : 'sans';
    if (!parSnapshot[cle]) parSnapshot[cle] = construire(snapshot, !!collecte);
    return parSnapshot[cle];
}
